export const routeMap = {
  home: '/',
  error: '/error',
  login: {
    root: '/login',
    SE: {
      bankid: '/login/bankid',
    },
    UK: {
      accountLocked: '/login/account-locked',
      authenticate: '/login/authenticate',
      manualApproval: '/login/manual-approval',
    },
    FI: {
      success: '/login/success',
      fail: {
        root: '/login/fail',
        noAccount: '/login/fail/no-account',
      },
    },
  },
  credit: {
    root: '/my-credit',
    deposit: {
      root: '/my-credit/deposit',
      UK: {
        summary: '/my-credit/deposit/summary',
      },
    },
    monthlyPayment: '/my-credit/monthly-payment',
    withdrawal: '/my-credit/withdrawal',
    tierMigration: {
      root: '/my-credit/tier-migration',
      id: (id = ':id') => ({
        bankId: `/my-credit/tier-migration/${id}/bankid`,
      }),
    },
    SE: {
      selectAccount: {
        root: '/my-credit/select-account',
        success: '/my-credit/select-account/success',
        migrationSuccess: '/my-credit/select-account/migration-success',
        failure: '/my-credit/select-account/failure',
      },
    },
    FI: {
      manualDeposit: '/my-credit/manual-deposit',
    },
  },
  debtConsolidation: {
    root: '/my-collected-loans',
    transfer: '/my-collected-loans/transfer',
    deposit: '/my-collected-loans/deposit',
    monthlyPayment: '/my-collected-loans/monthly-payment',
  },
  loan: {
    root: '/loan',
    monthlyPayment: '/loan/monthly-payment',
    deposit: '/loan/deposit',
    selectAccount: {
      root: '/loan/select-account',
      success: '/loan/select-account/success',
      failure: '/loan/select-account/failure',
    },
  },
  userProfile: {
    root: '/user-profile',
    accountDetails: '/user-profile/account-details',
    SE: {
      financialDetails: '/user-profile/financial-details',
      bankAccount: '/user-profile/bank-account',
      loanIdProtectTerminationInGracePeriod: {
        success: '/user-profile/loan-id-protect-termination-in-grace-period/success',
        failure: '/user-profile/loan-id-protect-termination-in-grace-period/failure',
      },
    },
    UK: {
      contactInformation: '/user-profile/contact-information',
      endAgreement: '/user-profile/end-agreement',
    },
  },
  transactions: {
    root: '/transactions',
    transactionId: (transactionId = ':transactionId') => `/transactions/${transactionId}`,
  },
  noActiveCredit: '/no-credit',
  levlaApply: '/levla/apply',
  // Shorthand. Only used to redirect to /payment-protection-insurance/signup
  ppi: '/ppi',
  paymentProtectionInsurance: {
    root: '/payment-protection-insurance',
    signup: '/payment-protection-insurance/signup',
  },
  idProtect: {
    root: '/id-protect',
    signup: '/id-protect/signup',
  },
  partner: (partnerId = ':partnerId') => ({
    application: {
      root: `/${partnerId}/application`,
      qrcode: `/${partnerId}/application/qrcode`,
      personalDetails: `/${partnerId}/application/personal-details`,
      employmentStatus: `/${partnerId}/application/employment-status`,
      living: `/${partnerId}/application/living`,
      loanPurpose: `/${partnerId}/application/loan-purpose`,
      finances: `/${partnerId}/application/finances`,
    },
    selectAccount: {
      root: `/partner/${partnerId}/select-account`,
      success: `/partner/${partnerId}/select-account/success`,
      failure: `/partner/${partnerId}/select-account/failure`,
    },
  }),
  application: {
    root: '/application',
    SE: {
      personalDetails: '/application/personal-details',
      employmentStatus: '/application/employment-status',
      living: '/application/living',
      loanPurpose: '/application/loan-purpose',
      addonProducts: '/application/addon-products',
      finances: '/application/finances',
      applicationId: (applicationId = ':applicationId') => ({
        bankId: `/application/${applicationId}/bankid`,
        openBanking: {
          choice: `/application/${applicationId}/open-banking/choice`,
          verification: `/application/${applicationId}/open-banking/verification`,
          connection: `/application/${applicationId}/open-banking/connection`,
          completion: `/application/${applicationId}/open-banking/completion`,
          recovery: `/application/${applicationId}/open-banking/recovery`,
        },
        status: `/application/${applicationId}/status`,
        levla: {
          offer: `/application/${applicationId}/levla/offer`,
          bankId: {
            root: `/application/${applicationId}/levla/bankid`,
            nordiskaRejection: `/application/${applicationId}/levla/bankid/nordiska-rejection`,
          },
        },
        credit: {
          offer: `/application/${applicationId}/credit/offer`,
          declineOffer: `/application/${applicationId}/credit/decline-offer`,
          openBankingDataNotProcessed: `/application/${applicationId}/credit/open-banking-data-not-processed`,
          bankId: {
            root: `/application/${applicationId}/credit/bankid`,
            nordiskaRejection: `/application/${applicationId}/credit/bankid/nordiska-rejection`,
          },
          signSuccess: `/application/${applicationId}/credit/sign-success`,
        },
        loan: {
          offer: `/application/${applicationId}/loan/offer`,
          bankId: `/application/${applicationId}/loan/bankid`,
          signSuccess: `/application/${applicationId}/loan/sign-success`,
        },
        loanIdProtect: {
          offer: `/application/${applicationId}/loan-id-protect/offer`,
        },
        partner: (partnerId = ':partnerId') => ({
          offer: {
            root: `/application/${applicationId}/partner/${partnerId}/offer`,
            selectedProductTier: `/application/${applicationId}/partner/${partnerId}/offer/selected-product-tier`,
          },
          bankId: `/application/${applicationId}/partner/${partnerId}/bankid`,
          signSuccess: `/application/${applicationId}/partner/${partnerId}/sign-success`,
        }),
      }),
    },
    UK: {
      address: '/application/address',
      income: '/application/income',
      outgoings: '/application/outgoings',
      status: {
        root: '/application/status',
        rejection: '/application/status/rejection',
        manualApproval: '/application/status/manual-approval',
      },
      creditLimit: '/application/credit-limit',
      broker: '/application/broker',
      mobileVerification: {
        root: '/application/mobile-verification',
        verify: '/application/mobile-verification/verify',
        rejection: '/application/mobile-verification/rejection',
      },
      documents: {
        root: '/application/documents',
        summary: '/application/documents/summary',
        creditAgreement: '/application/documents/credit-agreement',
        creditInformation: '/application/documents/credit-information',
      },
      success: '/application/success',
      accountLocked: '/application/account-locked',
    },
  },
  directPayment: '/direct-payment',
  fairnessReceipt: {
    transactionId: (transactionId = ':transactionId') => `/fairness-receipt/${transactionId}`,
  },
  brokerApplication: {
    applicationId: (applicationId = ':applicationId') => ({
      root: `/broker-application/${applicationId}`,
      SE: {
        landing: `/broker-application/${applicationId}/landing`,
        offer: `/broker-application/${applicationId}/offer`,
        declineOffer: `/broker-application/${applicationId}/decline-offer`,
        bankId: `/broker-application/${applicationId}/bankid`,
      },
    }),
  },
  UK: {
    depositResult: '/deposit-result',
    bankAccount: {
      root: '/bank-account',
      setup: '/bank-account/setup',
      directDebit: {
        root: '/bank-account/direct-debit',
        repaymentDate: '/bank-account/direct-debit/repayment-date',
      },
    },
  },
  dev: {
    createUser: '/dev/create-user',
  },
};
